import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { adminApi, type StudioUserDetail } from '../lib/api';
import { copy, formatLanguage, formatUserSubtitle, formatYesNo } from '../lib/copy';
import { cn, formatDate } from '../lib/utils';
import { ClientPagination, paginate } from '../components/ClientPagination';
import { ErrorState, LoadingState, PageHeader, StatusBadge } from '../components/ui';

type Tab = 'profile' | 'identities' | 'sessions' | 'legal';

export function StudioUserDetailPage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<Tab>('profile');
  const [identityPage, setIdentityPage] = useState(0);
  const identityPageSize = 10;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', 'studio', id],
    queryFn: () => adminApi.studioUser(id) as Promise<StudioUserDetail>,
    enabled: Boolean(id),
  });

  useEffect(() => {
    setIdentityPage(0);
  }, [id, tab]);

  if (isLoading) return <LoadingState label="Загрузка пользователя..." />;
  if (isError || !data) {
    return <ErrorState message={copy.errors.loadUser} onRetry={() => refetch()} />;
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'profile', label: copy.userDetail.profile },
    { id: 'identities', label: copy.userDetail.identities },
    { id: 'sessions', label: copy.userDetail.sessions },
    { id: 'legal', label: copy.userDetail.legal },
  ];
  const subtitle = formatUserSubtitle(data.user.username);

  return (
    <div>
      <Link to="/studio/users" className="mb-4 inline-flex items-center gap-1 text-sm text-ops-muted hover:text-ops-text">
        <ArrowLeft className="h-4 w-4" />
        {copy.userDetail.back}
      </Link>
      <PageHeader title={data.user.first_name} description={subtitle ?? undefined} />

      <div className="mb-4 flex gap-2 border-b border-ops-border">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn('ops-tab', tab === item.id ? 'ops-tab--active' : 'ops-tab--inactive')}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'profile' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="ops-card space-y-3 text-sm">
            <h2 className="text-sm font-semibold">{copy.userDetail.account}</h2>
            <dl className="space-y-2">
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.plan}</dt><dd><StatusBadge status={data.user.plan ?? 'studio'} kind="plan" /></dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.username}</dt><dd>{data.user.username ? `@${data.user.username}` : copy.common.notAvailable}</dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.telegram}</dt><dd>{data.user.telegram_id ? copy.userDetail.telegramConnected : copy.userDetail.telegramNotConnected}</dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.language}</dt><dd>{formatLanguage(data.user.language)}</dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.onboarded}</dt><dd>{formatYesNo(data.user.onboarded)}</dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.created}</dt><dd>{formatDate(data.user.created_at)}</dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.timezone}</dt><dd>{data.user.timezone}</dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">{copy.userDetail.memberships}</dt><dd>{data.user.memberships_count}</dd></div>
            </dl>
          </div>
          <div className="ops-card space-y-3 text-sm">
            <h2 className="text-sm font-semibold">Studio workspaces</h2>
            <dl className="space-y-2">
              <div className="flex justify-between"><dt className="text-ops-muted">Workspaces</dt><dd>{data.studio_workspaces.length}</dd></div>
              <div className="flex justify-between"><dt className="text-ops-muted">Memberships</dt><dd>{data.user.memberships_count}</dd></div>
            </dl>
          </div>
        </div>
      ) : null}

      {tab === 'identities' ? (
        <div className="ops-card overflow-hidden p-0">
          <table className="ops-table">
            <thead><tr><th>{copy.userDetail.provider}</th><th>{copy.userDetail.email}</th><th>{copy.userDetail.verified}</th><th>{copy.userDetail.lastUsed}</th></tr></thead>
            <tbody>{paginate(data.identities, identityPage, identityPageSize).map((identity) => <tr key={identity.id}><td><StatusBadge status={identity.provider} kind="provider" /></td><td>{identity.email ?? copy.common.notAvailable}</td><td>{identity.verified_at ? formatDate(identity.verified_at) : copy.common.no}</td><td className="text-ops-muted">{identity.last_used_at ? formatDate(identity.last_used_at) : copy.common.notAvailable}</td></tr>)}</tbody>
          </table>
          {data.identities.length > identityPageSize ? <ClientPagination page={identityPage} pageSize={identityPageSize} total={data.identities.length} onPageChange={setIdentityPage} /> : null}
        </div>
      ) : null}

      {tab === 'sessions' ? (
        <div className="ops-card overflow-hidden p-0">
          <table className="ops-table"><thead><tr><th>{copy.userDetail.sessionId}</th><th>{copy.userDetail.sessionCreated}</th><th>{copy.userDetail.sessionExpires}</th></tr></thead><tbody>{data.sessions.map((session) => <tr key={session.id}><td className="font-mono text-xs">{session.id}</td><td>{formatDate(session.created_at)}</td><td>{formatDate(session.expires_at)}</td></tr>)}</tbody></table>
        </div>
      ) : null}

      {tab === 'legal' ? (
        <div className="ops-card overflow-hidden p-0">
          <table className="ops-table"><thead><tr><th>{copy.userDetail.legalSource}</th><th>{copy.userDetail.legalAccepted}</th><th>{copy.userDetail.legalOffer}</th><th>{copy.userDetail.legalPrivacy}</th><th>{copy.userDetail.legalConsent}</th></tr></thead><tbody>{data.legal_acceptances.map((row) => <tr key={row.id}><td>{row.source}</td><td>{formatDate(row.accepted_at)}</td><td>{row.offer_version}</td><td>{row.privacy_version}</td><td>{row.consent_version}</td></tr>)}</tbody></table>
        </div>
      ) : null}
    </div>
  );
}
